import { SerializeTextLeaf } from '../textLeaf';
import type { BasicText, InlineNode } from '../types';
import { isHardBreakNode } from '../utils/isHardBreakNode';
import { BulletList, OrderedList } from './List';
import { ListItem } from './ListItem';
import { ParagraphElement } from './Paragraph';
import type { ParagraphElementProps } from './Paragraph.types';

const bulletListIdentifierRegex = /^\s*([-—*•])\s/;
const orderedListIdentifierRegex = /^\s*(\d+)\.\s/;

type ParagraphGroup =
  | { type: 'ordered' | 'bullet'; items: Array<InlineNode[]> }
  | { type: 'basic'; items: InlineNode[] };

const ParagraphListSerializer = ({ element, children, ...restProps }: ParagraphElementProps) => {
  const { content } = element;

  if (!content) {
    return (
      <ParagraphElement {...restProps} element={element}>
        {children}
      </ParagraphElement>
    );
  }

  // Group consecutive list items together
  const groups: ParagraphGroup[] = [];
  let currentGroup: (typeof groups)[0] | null = null;
  let isLastPushedItemHardBreak = false;

  content.forEach((node, index) => {
    if (isHardBreakNode(node)) {
      if (currentGroup) {
        if (currentGroup.type === 'basic') {
          currentGroup.items.push(node);
        } else {
          if (isLastPushedItemHardBreak) {
            groups.push(currentGroup);
            currentGroup = { type: 'basic', items: [node] };
          } else {
            currentGroup.items[currentGroup.items.length - 1].push(node);
            currentGroup.items.push([]);
          }
        }
        isLastPushedItemHardBreak = true;
      } else {
        groups.push({ type: 'basic', items: [node] });
        currentGroup = groups[groups.length - 1];
        isLastPushedItemHardBreak = true;
      }
      return;
    }

    if ('text' in node) {
      const isOrderedList = orderedListIdentifierRegex.test(node.text);
      const isBulletList = bulletListIdentifierRegex.test(node.text);

      let type: ParagraphGroup['type'];

      if (isOrderedList) {
        type = 'ordered';
      } else if (isBulletList) {
        const nodeBefore = content[index - 1];

        const allowBulletList = nodeBefore ? isHardBreakNode(nodeBefore) || !nodeBefore.text.trim().length : true;

        type = allowBulletList ? 'bullet' : 'basic';
      } else {
        type = 'basic';
      }

      const shouldStartNewGroup =
        !currentGroup ||
        (isLastPushedItemHardBreak && type === 'basic') ||
        (currentGroup.type !== type && type !== 'basic');

      if (shouldStartNewGroup) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        if (type === 'basic') {
          currentGroup = { type, items: [node] };
        } else {
          currentGroup = { type, items: [[node]] };
        }
        isLastPushedItemHardBreak = false;
      } else {
        if (currentGroup?.type === 'basic') {
          currentGroup.items.push(node);
        } else {
          currentGroup?.items[currentGroup.items.length - 1].push(node);
        }
        isLastPushedItemHardBreak = false;
      }
    }
  });

  if (currentGroup) {
    groups.push(currentGroup);
  }

  groups.forEach((group) => {
    if (!['ordered', 'bullet'].includes(group.type)) {
      return;
    }

    const lastItem = group.items.at(-1);

    if (Array.isArray(lastItem) && lastItem.length === 0) {
      group.items.pop();
    }
  });

  return (
    <>
      {groups.map((group, groupIndex) => {
        if (group.type === 'basic') {
          return (
            <ParagraphElement
              key={groupIndex}
              {...restProps}
              element={{
                ...element,
                content: group.items,
              }}
            >
              {group.items.map((item, itemIndex) =>
                isHardBreakNode(item) ? (
                  <br key={`br${itemIndex}`} />
                ) : (
                  <SerializeTextLeaf leaf={item} key={`br${itemIndex}`} />
                ),
              )}
            </ParagraphElement>
          );
        }

        const ListTag = group.type === 'ordered' ? OrderedList : BulletList;
        return (
          <ParagraphElement
            key={groupIndex}
            {...restProps}
            element={{
              ...element,
            }}
          >
            <ListTag {...restProps} element={element}>
              {group.items.map((itemGroup, index) => (
                <ListItem
                  key={index}
                  inlineNodes={itemGroup.map((item) =>
                    isHardBreakNode(item)
                      ? item
                      : {
                          ...item,
                          text: item.text.replace(
                            group.type === 'ordered' ? orderedListIdentifierRegex : bulletListIdentifierRegex,
                            '',
                          ),
                        },
                  )}
                />
              ))}
            </ListTag>
          </ParagraphElement>
        );
      })}
    </>
  );
};

export const ParagraphSerializer = (props: ParagraphElementProps) => {
  const { element } = props;
  const { content } = element;

  const hasList = content
    ?.filter((content): content is BasicText => !isHardBreakNode(content))
    .some(({ text }) => bulletListIdentifierRegex.test(text) || orderedListIdentifierRegex.test(text));

  if (hasList) {
    return <ParagraphListSerializer {...props} />;
  }

  return <ParagraphElement {...props} />;
};
